import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// 所有请求统一走 /api，由 Vite proxy（开发）或 Cloudflare Pages Function（生产）代理到后端
const axiosInstance = axios.create({
  baseURL: '/api',
  timeout: 300000,
});

// TTS/ASR 长时间请求直连后端（绕过 Cloudflare Workers 的 CPU 时间限制）
// 后端地址从 /config 接口获取（复用 Cloudflare 的 BACKEND_URL 环境变量）
let directAxios: ReturnType<typeof axios.create> | null = null;

export async function initDirectBackend() {
  try {
    const res = await fetch('/config');
    const data = await res.json();
    const url = data.backend_url;
    if (url) {
      const instance = axios.create({ baseURL: url, timeout: 300000 });
      // 直连时需要手动注入 HF Token（原本由 Cloudflare Function 代理注入）
      instance.interceptors.request.use((config) => {
        const hfToken = data.hf_token;
        if (hfToken) {
          config.headers.Authorization = `Bearer ${hfToken}`;
        }
        // 同时传递用户的 API Key
        const apiKey = useAuthStore.getState().apiKey;
        if (apiKey) {
          config.headers['X-API-Key'] = apiKey;
        }
        return config;
      });
      directAxios = instance;
      console.log('Direct backend initialized:', url);
    }
  } catch (e) {
    console.warn('Failed to init direct backend, falling back to proxy:', e);
  }
}

// 请求拦截器 - 只需处理应用自身的 API Key 认证
// HF Token 认证由代理层（Vite proxy / Cloudflare Function）处理
axiosInstance.interceptors.request.use(
  (config) => {
    const { apiKey } = useAuthStore.getState();
    if (apiKey) {
      config.headers.Authorization = `Bearer ${apiKey}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器 - 处理错误
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // 认证失败，可能是 API Key 或 HF Token 无效
      const errorMessage = error.response?.data?.detail || '';
      
      if (errorMessage.includes('HF') || errorMessage.includes('huggingface')) {
        // HF Token 错误，不清除登录状态
        console.error('HF Token 验证失败:', errorMessage);
      } else {
        // API Key 错误，登出
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// 认证 API
export const authApi = {
  login: (username: string, password: string) =>
    axiosInstance.post('/auth/login', { username, password }),

  getProfile: () =>
    axiosInstance.get('/auth/profile'),

  // 注册
  register: (username: string, email: string, password: string, invitationCode?: string) =>
    axiosInstance.post('/auth/register', { username, email, password, invitation_code: invitationCode }),

  // 获取公开设置（无需认证）
  getPublicSettings: () =>
    axiosInstance.get('/public/settings'),

  // 验证邮箱
  verifyEmail: (email: string, code: string) =>
    axiosInstance.post('/auth/verify-email', { email, code }),

  // 找回密码
  forgotPassword: (email: string) =>
    axiosInstance.post('/auth/forgot-password', { email }),

  // 重置密码
  resetPassword: (email: string, code: string, new_password: string) =>
    axiosInstance.post('/auth/reset-password', { email, code, new_password }),
};

// TTS API
// TTS/ASR 使用直连后端的 axios 实例（避免 Cloudflare Workers 超时）
// 使用 getter 动态获取，因为 directAxios 是异步初始化的
const getTtsAxios = () => directAxios || axiosInstance;

export const ttsApi = {
  voiceDesign: (data: FormData) =>
    getTtsAxios().post('/tts/voice-design', data, {
      responseType: 'blob',
    }),

  controllableClone: (data: FormData) =>
    getTtsAxios().post('/tts/controllable-clone', data, {
      responseType: 'blob',
    }),

  ultimateClone: (data: FormData) =>
    getTtsAxios().post('/tts/ultimate-clone', data, {
      responseType: 'blob',
    }),

  transcribe: (data: FormData) =>
    getTtsAxios().post('/asr/transcribe', data),
};

// 管理员 API
export const adminApi = {
  getUsers: () =>
    axiosInstance.get('/admin/users'),

  createUser: (data: { username: string; password: string; role: string; daily_quota: number }) =>
    axiosInstance.post('/admin/users', data),

  updateUser: (username: string, data: Partial<{ role: string; daily_quota: number; is_active: boolean }>) =>
    axiosInstance.put(`/admin/users/${username}`, data),

  deleteUser: (username: string) =>
    axiosInstance.delete(`/admin/users/${username}`),

  getStats: () =>
    axiosInstance.get('/admin/stats'),

  getUsage: (params?: { username?: string; limit?: number }) =>
    axiosInstance.get('/admin/usage', { params }),

  // 邮箱配置
  getEmailConfig: () =>
    axiosInstance.get('/admin/email-config'),

  updateEmailConfig: (data: {
    smtp_host: string;
    smtp_port: number;
    smtp_user: string;
    smtp_password: string;
    smtp_use_tls: boolean;
    sender_name: string;
    sender_email: string;
  }) =>
    axiosInstance.put('/admin/email-config', data),

  testEmailConfig: (toEmail: string) =>
    axiosInstance.post('/admin/email-config/test', { to_email: toEmail }),

  // 邀请码管理
  invitationCodes: {
    list: () => axiosInstance.get('/admin/invitation-codes'),
    generate: (count?: number, maxUses?: number) =>
      axiosInstance.post('/admin/invitation-codes', { count, max_uses: maxUses }),
    delete: (code: string) =>
      axiosInstance.delete(`/admin/invitation-codes/${code}`),
  },

  // VIP 兑换码管理
  vipCodes: {
    list: () => axiosInstance.get('/admin/vip-codes'),
    generate: (count?: number) =>
      axiosInstance.post('/admin/vip-codes', { count }),
    delete: (code: string) =>
      axiosInstance.delete(`/admin/vip-codes/${code}`),
  },

  // 系统设置
  settings: {
    get: () => axiosInstance.get('/admin/settings'),
    update: (settings: any) => axiosInstance.put('/admin/settings', settings),
  },
};

// Access Token API
export const tokenApi = {
  list: () =>
    axiosInstance.get('/auth/tokens'),

  create: (name: string) =>
    axiosInstance.post('/auth/tokens', { name }),

  revoke: (tokenId: number) =>
    axiosInstance.delete(`/auth/tokens/${tokenId}`),
};

// 用户 VIP 兑换 API
export const vipApi = {
  redeem: (code: string) =>
    axiosInstance.post('/auth/redeem-vip', { code }),
};

// 导出统一的 API 对象
export const api = {
  // 认证相关
  login: authApi.login,
  getProfile: authApi.getProfile,
  register: authApi.register,
  verifyEmail: authApi.verifyEmail,
  forgotPassword: authApi.forgotPassword,
  resetPassword: authApi.resetPassword,

  // TTS 相关
  voiceDesign: ttsApi.voiceDesign,
  controllableClone: ttsApi.controllableClone,
  ultimateClone: ttsApi.ultimateClone,
  transcribe: ttsApi.transcribe,

  // 管理员相关
  getUsers: adminApi.getUsers,
  createUser: adminApi.createUser,
  updateUser: adminApi.updateUser,
  deleteUser: adminApi.deleteUser,
  getStats: adminApi.getStats,
  getUsage: adminApi.getUsage,
  getEmailConfig: adminApi.getEmailConfig,
  updateEmailConfig: adminApi.updateEmailConfig,
  testEmailConfig: adminApi.testEmailConfig,
  invitationCodes: adminApi.invitationCodes,
  settings: adminApi.settings,

  // Access Token 相关
  tokens: tokenApi,
};

export const extractApiErrorMessage = async (error: any, fallback: string) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }

  const responseData = error?.response?.data;
  if (responseData instanceof Blob) {
    try {
      const text = await responseData.text();
      if (!text) {
        return fallback;
      }

      const parsed = JSON.parse(text);
      if (typeof parsed?.detail === 'string' && parsed.detail.trim()) {
        return parsed.detail;
      }
    } catch {
      return fallback;
    }
  }

  return fallback;
};

export default api;
